import { ReactNode } from "react"
import {
  Button,
  Image,
  Modal,
  ModalBody,
  ModalBodyProps,
  ModalContent,
  ModalOverlay,
} from "@chakra-ui/react"

import closeButtonIconDefault from "#/assets/close-button.svg"

import { BACKGROUND_MODAL_Z_INDEX, DEFAULT_MODAL_Z_INDEX } from "../constants"

type ModalContainerProps = {
  children: ReactNode
  isOpen: boolean
  onClose: () => void
  isCentered?: boolean
  width?: string
  /**
   * Optional flag to determine if the modal should act as a background layer.
   * Helpful when we want to display something on top of the modal - it will
   * lower it's z-indx (which is high by default in Chakra) and remove the focus
   * from it.
   */
  isBackgroundModal?: boolean
  props?: ModalBodyProps
  closeButtonIcon?: string
}

export default function ModalContainer({
  children,
  isOpen,
  onClose,
  isCentered = true,
  width = "522px",
  isBackgroundModal = false,
  props,
  closeButtonIcon = closeButtonIconDefault,
}: ModalContainerProps) {
  // When it's a background modal, the zIndex has to be lower than 89 which is
  // z-index for WalletConnect modal. Thanks to that, the wallet connect modal
  // will be displayed on top of this background modal.
  const zIndex = isBackgroundModal
    ? BACKGROUND_MODAL_Z_INDEX
    : DEFAULT_MODAL_Z_INDEX

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick
      isCentered={isCentered}
      // We need to disable setting "aria-hidden" to true prevent
      // focus-fighting detection
      useInert={false}
      // We should disable focus lock to be able to apply snackbar
      // components
      trapFocus={false}
      blockScrollOnMount={!isBackgroundModal}
    >
      <ModalOverlay bg="primary.black.80" zIndex={zIndex} />
      <Button
        variant="unstyled"
        zIndex={zIndex + 1}
        pos="fixed"
        cursor="pointer"
        top="51px"
        right="56px"
        onClick={onClose}
        h="40px"
        w="40px"
        p={0}
      >
        <Image src={closeButtonIcon} />
      </Button>
      <ModalContent
        minW={width}
        bg="none"
        boxShadow="none"
        zIndex={zIndex}
        containerProps={{
          zIndex,
        }}
      >
        <ModalBody w={width} p={0} {...props}>
          {children}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
